import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { T, F, th as palette, Btn } from "@/lib/bricks";
import { Page, PageHeader, Empty, Loading, TextLink, List, Row, Meta, Section } from "@/components/calm/Calm";
import { PackageDialog, CopyGenDialog } from "@/pages/MLSPage";
import { stateLabel, secondaryAction, progressLine, subtitle, fileName, materialUrl } from "@/components/launch/kit";

// Listing launch kit: what this listing has (media, site, Virtual Visit, copy, MLS package) and the one thing to do next.
// Read only. The MLS step never sends anything to a board; the agent submits in the board's own system.
export default function LaunchKitPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === "dark";
  const p = palette(dark);
  const [kit, setKit] = useState(null);
  const [listing, setListing] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | off | missing
  const [dialog, setDialog] = useState(null); // package | copy
  const [working, setWorking] = useState(false);
  const [fetching, setFetching] = useState(null); // material key being downloaded
  const [options, setOptions] = useState({}); // material key -> option on (e.g. the social pack's address line)

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` };

  const load = async () => {
    const res = await fetch(`${BASE}/api/mls/listings/${id}/launch`, { headers }).catch(() => null);
    if (!res || !res.ok) {
      const d = res ? await res.json().catch(() => ({})) : {};
      setState(d?.detail?.code === "not_enabled" || !res ? "off" : "missing");
      return;
    }
    setKit(await res.json());
    setState("ready");
    fetch(`${BASE}/api/mls/listings`, { headers }).then((r) => r.json())
      .then((rows) => setListing((Array.isArray(rows) ? rows : []).find((l) => l.id === id) || null)).catch(() => {});
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch (e) { toast(url); }
  };

  // Files are built on request from the listing's own fields and delivered photos; the agent uses them themselves.
  const download = async (m) => {
    setFetching(m.key);
    try {
      const res = await fetch(`${BASE}${materialUrl(m, options[m.key])}`, { headers: { Authorization: headers.Authorization } });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.detail?.message || "Couldn't build the file. Try again.");
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName(res.headers.get("Content-Disposition"), m.filename || m.href.split("/").pop());
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      toast.success(`${m.title} downloaded`);
    } catch (e) {
      toast.error(e.message || "Couldn't build the file. Try again.");
    } finally { setFetching(null); }
  };

  const run = async (action) => {
    if (!action) return;
    if (action.kind === "copy_link") return copyLink(action.url);
    if (action.kind === "link_deal") return navigate(`/mls?link=${id}`);
    if (action.kind === "write_copy") return setDialog("copy");
    if (action.kind === "open_package" || action.kind === "edit_copy") return setDialog("package");
    if (action.kind === "book") {
      setWorking(true);
      try {
        const res = await fetch(`${BASE}/api/book/handoff`, { method: "POST", headers, body: JSON.stringify({ deal_id: kit.listing.deal_id, focus: action.focus }) });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || !d.url) throw new Error();
        window.open(d.url, "_blank", "noopener");
      } catch (e) {
        toast.error("Couldn't open booking. Try again.");
      } finally { setWorking(false); }
    }
  };

  const back = <TextLink hit testid="launch-back" onClick={() => navigate("/mls")}>Back to listings</TextLink>;
  if (state === "loading") return <Loading />;
  if (state === "off" || state === "missing") {
    return (
      <Page testid="launch-kit-page">
        <PageHeader title="Launch kit" />
        <Empty action={back}>{state === "off" ? "The launch kit isn't switched on for your office." : "This listing isn't in your office."}</Empty>
      </Page>
    );
  }

  const next = kit.next;
  return (
    <Page testid="launch-kit-page" width={880}>
      <PageHeader
        title="Launch kit"
        subtitle={subtitle(kit)}
        action={next && !dialog ? <Btn variant="primary" dark={dark} data-testid="launch-next" style={{ whiteSpace: "nowrap" }} disabled={working} onClick={() => run(next)}>{working ? "Opening…" : next.label}</Btn> : null}
      />
      <div data-testid="launch-progress" style={{ fontFamily: F.mono, fontSize: 11.5, letterSpacing: 0.2, color: p.ink3, margin: "0 0 12px" }}>
        {progressLine(kit)}
      </div>
      <List testid="launch-steps">
        {kit.steps.map((s) => {
          const extra = secondaryAction(s, next);
          const isNext = next && next.step === s.key;
          return (
            <Row key={s.key} testid={`launch-step-${s.key}`}
              title={<span style={{ display: "inline-flex", gap: 12, alignItems: "baseline" }}>
                <span style={{ fontFamily: F.mono, fontSize: 11.5, color: isNext ? T.brickDeep : p.ink3 }}>{s.no}</span>{s.title}
              </span>}
              meta={s.detail}
              next={(s.note || extra || s.url) ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingLeft: 30 }}>
                  {s.url ? <Meta parts={[{ text: <span style={{ fontFamily: F.mono, fontSize: 11.5, overflowWrap: "anywhere" }}>{s.url.replace(/^https?:\/\//, "")}</span> }]} /> : null}
                  {s.note ? <div style={{ fontSize: 12.5, color: p.ink3 }}>{s.note}</div> : null}
                  {extra ? <span><TextLink hit testid={`launch-${s.key}-action`} onClick={() => run(extra)}>{extra.label}</TextLink></span> : null}
                </div>) : null}
              status={stateLabel(s.state)}
              attention={isNext} />
          );
        })}
      </List>
      {kit.materials?.length ? (
        <div style={{ marginTop: 32 }}>
          <Section title="Materials" testid="launch-materials" note="Made from this listing's details and delivered photos. Download and share them yourself.">
            <List>
              {kit.materials.map((m) => (
                <Row key={m.key} testid={`launch-material-${m.key}`} title={m.title} meta={m.meta}
                  status={m.ready ? null : m.note}
                  next={m.ready && m.option ? (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: p.ink2, cursor: "pointer" }}>
                      <input type="checkbox" data-testid={`launch-option-${m.key}`} checked={!!options[m.key]}
                        onChange={(e) => setOptions((o) => ({ ...o, [m.key]: e.target.checked }))}
                        style={{ accentColor: p.ink, width: 14, height: 14, margin: 0 }} />
                      {m.option.label}
                    </label>) : null}
                  aside={m.ready ? (
                    <TextLink hit testid={`launch-download-${m.key}`} onClick={() => fetching ? null : download(m)}>
                      {fetching === m.key ? "Preparing…" : m.label}
                    </TextLink>) : null} />
              ))}
            </List>
          </Section>
        </div>) : null}
      <div style={{ marginTop: 28 }}>{back}</div>

      {dialog === "package" && listing && (
        <PackageDialog listing={listing} initialBoard={kit.steps.find((s) => s.key === "mls")?.board} dark={dark} p={p}
          session={session} BASE={BASE} onClose={() => setDialog(null)} onChanged={load} />
      )}
      {dialog === "copy" && listing && (
        <CopyGenDialog listing={listing} dark={dark} p={p} session={session} BASE={BASE}
          onClose={() => setDialog(null)} onSaved={load} />
      )}
    </Page>
  );
}
